// ** React Imports
import { useEffect, useState } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Chip,
  FormLabel,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// ** Icon Imports
import Icon from "components/icon";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";
import InputAdornment from "@mui/material/InputAdornment";

// ** Logic Imports
import { useServicePermission } from "../permissions/ServicePermissions";
import { useIndustryLogic } from "screens/Settings/logic/industry-logic";
import { useSettingPermission } from "screens/Settings/permissions/SettingPermissions";
import { useDefaultResourceLogic } from "../logic/default-resource-logic";

// ** Dialog Imports
import AddIndustryDialog from "screens/Settings/component/AddIndustry";

// ** Third-Party Imports
import { toast, ToastContainer } from "react-toastify";

// ** Other Imports
import "assets/CSS/Switch.css";

// ** Unit Dialog Import
import UnitDialog from "screens/Inventory/component/UnitofMeasurement";

const ServiceConfiguration = ({
  open,
  handleClose,
  serviceContent,
  ServiceProps,
}) => {
  // State for expandable sections
  const [expandedSections, setExpandedSections] = useState({
    materials: true,
    labor: true,
    assets: true,
    otherCosts: true,
    subcontractor: true
  });

  // State for Unit Dialog
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [unitCallback, setUnitCallback] = useState(null);

  //#region Permissions
  const { ServicePermissionProps } = useServicePermission();
  const { hasServiceSavePermission } = ServicePermissionProps;
  //#endregion Permissions

  const { SettingPermissionProps } = useSettingPermission();
  const {
    hasIndustrySavePermission,
  } = SettingPermissionProps;

  const { IndustryProps } = useIndustryLogic(toast);
  const {
    handleOpenIndustryDlg,
    fetchIndustries,
    openIndustryDlg,
    handleCloseIndustryDlg,
    rows,
  } = IndustryProps;

  const {
    handleSaveService,
    handleServiceChange,
    service,
    serviceErrors,
    ResetService,
  } = ServiceProps;

  const { DefaultResourceProps } = useDefaultResourceLogic();
  const {
    allMaterials,
    materialTypes,
    allAssets,
    assetTypes,
    skillTypes,
    allEmployees,
    subcontractors,
    fetchDefaultResources,
    defaultAssets,
    setDefaultAssets,
    defaultMaterials,
    setDefaultMaterials,
    defaultLabors,
    setDefaultLabors,
    defaultSubcontractors,
    setDefaultSubcontractors,
    defaultMiscCosts,
    setDefaultMiscCosts,
    resetDefaultResources,
    handleDeleteDefaultMaterial,
    handleDeleteDefaultAsset,
    handleDeleteDefaultLabor,
    handleDeleteDefaultSubcontractor,
    handleDeleteDefaultMiscCost,
    units, // Make sure this is coming from your logic hook
    fetchUnitsData, // Make sure this is coming from your logic hook
  } = DefaultResourceProps;

  const theme = useTheme();

  useEffect(() => {
    if (open) {
      fetchIndustries();
      // Fetch units when dialog opens
      if (fetchUnitsData) {
        fetchUnitsData();
      }
    }
  }, [open]);

  useEffect(() => {
    if (service?.serviceOfferingID) {
      fetchDefaultResources(service.serviceOfferingID);
    }
  }, [service?.serviceOfferingID]);

  const handleCloseControl = () => {
    ResetService();
    resetDefaultResources();
    handleClose();
  };

  // Function to reset both service details and resource details
  const handleResetAll = () => {
    ResetService(); // Reset service details
    resetDefaultResources(); // Reset resource details
    
    // Reset to initial state for all resources
    setDefaultMaterials([{
      id: 1,
      serviceOfferingID: null,
      materialID: null,
      materialTypeID: null,
      quantity: 0,
      unitID: null,
      costPerUnit: 0,
      pricePerUnit: 0,
    }]);
    
    setDefaultLabors([{
      id: 1,
      serviceOfferingID: null,
      employeeID: null,
      skillID: null,
      manHours: 0,
      costPerHour: 0,
      markup: 0,
    }]);

    setDefaultSubcontractors([{
      id: 1,
      serviceOfferingID: null,
      employeeID: null,
      skillID: null,
      unitID: 0,
      unitQuantity: 0,
      costPerUnit: 0,
      pricePerUnit: 0,
      markup: 0,
    }]);
    
    setDefaultAssets([{
      id: 1,
      serviceOfferingID: null,
      assetTypeID: null,
      assetID: null,
      quantity: 0,
      usageDuration: 0,
      costPerUnit: 0,
      pricePerUnit: 0,
    }]);
    
    setDefaultMiscCosts([{
      id: 1,
      serviceOfferingID: null,
      costName: "",
      costAmount: 0,
      customerPrice: 0,
      billToCustomer: false,
    }]);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Utility function to remove leading zeros
  const removeLeadingZeros = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    
    const str = value.toString().trim();
    
    // Handle decimal numbers
    if (str.includes('.')) {
      const parts = str.split('.');
      // Remove leading zeros from integer part, but keep at least one zero if it's just "0."
      parts[0] = parts[0].replace(/^0+/, '') || '0';
      return parts.join('.');
    }
    
    // Handle integers - remove leading zeros but keep at least one zero if the value is just "0"
    return str.replace(/^0+/, '') || '0';
  };

  // Handle opening unit dialog
  const handleOpenUnitDialog = (callback) => {
    setUnitCallback(() => callback);
    setUnitDialogOpen(true);
  };

  // Handle unit creation callback
  const handleUnitCreatedCallback = (result) => {
    if (unitCallback) {
      unitCallback(result);
    }
    setUnitDialogOpen(false);
    setUnitCallback(null);
    // Refresh units list
    if (fetchUnitsData) {
      fetchUnitsData();
    }
  };

  // Prepare unit options including "Add New" option
  const unitOptions = [
    { value: '__ADD_NEW_UNIT__', label: '+ Create New Unit', isAddNew: true },
    ...(units || []).map(unit => ({
      value: unit.value,
      label: unit.label
    }))
  ];

  // Handle unit selection with "Add New" option for Material
  const handleMaterialUnitChange = (materialId, newValue) => {
    if (newValue?.value === '__ADD_NEW_UNIT__') {
      // Open unit dialog with callback to update material unit
      handleOpenUnitDialog((result) => {
        if (result && result.unitName) {
          updateMaterial(materialId, "unitID", result.unitID);
        }
      });
    } else {
      // Set the selected unit
      updateMaterial(materialId, "unitID", newValue?.value || "");
    }
  };

  const handleSubcontractorUnitChange = (subcontractorId, newValue) => {
    if (newValue?.value === '__ADD_NEW_UNIT__') {
      // Open unit dialog with callback to update material unit
      handleOpenUnitDialog((result) => {
        if (result && result.unitName) {
          updateSubcontractor(subcontractorId, "unitID", result.unitID);
        }
      });
    } else {
      // Set the selected unit
      updateSubcontractor(subcontractorId, "unitID", newValue?.value || "");
    }
  };

  // Material functions
  const addMaterial = () => {
    const highestId = defaultMaterials.reduce((max, material) => {
      const currentId = material.defaultJobMaterialID || material.id;
      return currentId > max ? currentId : max;
    }, 0);
  
    const newId = highestId + 1;

    setDefaultMaterials([
      ...defaultMaterials,
      {
        id: newId,
        serviceOfferingID: null,
        materialID: null,
        materialTypeID: null,
        quantity: 0,
        unitID: 0,
        costPerUnit: 0,
        pricePerUnit: 0,
      },
    ]);
  };

  const removeMaterial = (id) => {
    if (defaultMaterials.length > 1) {
      const materialToDelete = defaultMaterials.find((dm) => dm.id === id);

      if(materialToDelete.defaultJobMaterialID && materialToDelete.defaultJobMaterialID > 0){
        const response = handleDeleteDefaultMaterial(id);

        if(response){
          fetchDefaultResources(service.serviceOfferingID)
        }
      }else{
        setDefaultMaterials(
          defaultMaterials.filter((material) => material.id !== id)
        );
      }
    }
  };

  const updateMaterial = (id, field, value) => {
    if(field === 'materialID'){
      const selectedMaterial = allMaterials?.find((m) => m.materialID === value);

      setDefaultMaterials(
        defaultMaterials?.map((material) =>
          material.id === id ? { 
            ...material,
            [field]: value,
            unitID: selectedMaterial?.unitID > 0 ? selectedMaterial.unitID : 0,
            costPerUnit: selectedMaterial?.costPerUnit,
            pricePerUnit: selectedMaterial?.perUnitPrice,
          } : material
        )
      );
    }else{
      setDefaultMaterials(
        defaultMaterials?.map((material) =>
          material.id === id ? { ...material, [field]: value } : material
        )
      );
    }
  };

  // Labor functions
  const addLabor = () => {
    const highestId = defaultLabors.reduce((max, labor) => {
      const currentId = labor.defaultJobLaborID || labor.id;
      return currentId > max ? currentId : max;
    }, 0);
  
    const newId = highestId + 1;

    setDefaultLabors([
      ...defaultLabors,
      {
        id: newId,
        serviceOfferingID: null,
        employeeID: null,
        skillID: null,
        manHours: 0,
        costPerHour: 0,
        markup: 0,
      },
    ]);
  };

  const removeLabor = (id) => {
    if (defaultLabors.length > 1) {
      const laborToDelete = defaultLabors.find((dm) => dm.id === id);

      if(laborToDelete.defaultJobLaborID && laborToDelete.defaultJobLaborID > 0){
        const response = handleDeleteDefaultLabor(id);

        if(response){
          fetchDefaultResources(service.serviceOfferingID)
        }
      }else{
        setDefaultLabors(defaultLabors.filter((labor) => labor.id !== id));
      }
    }
  };

  const updateLabor = (id, field, value) => {
    if(field === 'employeeID'){
      const selectedLabor = allEmployees?.find((e) => e.employeeID === value);

      setDefaultLabors(
        defaultLabors?.map((labor) =>
          labor.id === id ? {
            ...labor,
            [field]: value,
            costPerHour: selectedLabor?.wageType !== "Salary" ? selectedLabor?.wageAmount : 0
          } : labor
        )
      );
    } else {
      setDefaultLabors(
        defaultLabors?.map((labor) =>
          labor.id === id ? { ...labor, [field]: value } : labor
        )
      );
    }
  };

  // Subcontractor functions
  const addSubcontractor = () => {
    const highestId = defaultSubcontractors.reduce((max, subcontractor) => {
      const currentId = subcontractor.defaultJobSubcontractorID || subcontractor.id;
      return currentId > max ? currentId : max;
    }, 0);
  
    const newId = highestId + 1;

    setDefaultSubcontractors([
      ...defaultSubcontractors,
      {
        id: newId,
        serviceOfferingID: null,
        employeeID: null,
        skillID: null,
        unitID: null,
        unitQuantity: 0,
        costPerUnit: 0,
        pricePerUnit: 0,
        markup: 0,
      },
    ]);
  };

  const removeSubcontractor = (id) => {
    if (defaultSubcontractors.length > 1) {
      const subcontractorToDelete = defaultSubcontractors.find((dm) => dm.id === id);

      if(subcontractorToDelete.defaultJobSubcontractorID && subcontractorToDelete.defaultJobSubcontractorID > 0){
        const response = handleDeleteDefaultSubcontractor(id);

        if(response){
          fetchDefaultResources(service.serviceOfferingID)
        }
      }else{
        setDefaultSubcontractors(defaultSubcontractors.filter((subcontractor) => subcontractor.id !== id));
      }
    }
  };

  const updateSubcontractor = (id, field, value) => {
    // if(field === 'employeeID'){
    //   const selectedSubcontractor = subcontractors?.find((e) => e.employeeID === value);

    //   setDefaultSubcontractors(
    //     defaultSubcontractors?.map((subcontractor) =>
    //       subcontractor.id === id ? {
    //         ...subcontractor,
    //         [field]: value,
    //         unitType: selectedSubcontractor?.wageType || 'Hourly',
    //         costPerUnit: selectedSubcontractor?.wageType !== "Salary" ? selectedSubcontractor?.wageAmount : 0,
    //         unitQuantity: 1
    //       } : subcontractor
    //     )
    //   );
    //     } else if(field === 'pricePerUnit'){
        if(field === 'pricePerUnit'){
        setDefaultSubcontractors(
          defaultSubcontractors?.map((subcontractor) =>
            subcontractor.id === id ? { 
              ...subcontractor, 
              [field]: value,
              markup: subcontractor.costPerUnit && value
                  ? Math.round(((parseFloat(value) - parseFloat(subcontractor.costPerUnit)) / parseFloat(subcontractor.costPerUnit)) * 100)
                  : 0
            } : subcontractor
          )
        );
      } else if(field === 'markup'){
      setDefaultSubcontractors(
        defaultSubcontractors?.map((subcontractor) =>
          subcontractor.id === id ? { 
            ...subcontractor, 
            [field]: value,
            pricePerUnit: subcontractor.costPerUnit && value
                ? parseFloat(subcontractor.costPerUnit) * (1 + parseFloat(value) / 100)
                : 0
          } : subcontractor
        )
      );
    } else if(field === 'costPerUnit'){
      setDefaultSubcontractors(
        defaultSubcontractors?.map((subcontractor) =>
          subcontractor.id === id ? { 
            ...subcontractor, 
            [field]: value,
            pricePerUnit: value && subcontractor.markup
                ? parseFloat(value) * (1 + parseFloat(subcontractor.markup) / 100)
                : subcontractor.pricePerUnit
          } : subcontractor
        )
      );
    } else{
      setDefaultSubcontractors(
        defaultSubcontractors?.map((subcontractor) =>
          subcontractor.id === id ? { ...subcontractor, [field]: value } : subcontractor
        )
      );
    }
  };

  useEffect(() => {
    console.log('defaultSubcontractors', defaultSubcontractors)
  }, [defaultSubcontractors])
  
  // Asset functions
  const addAsset = () => {
    const highestId = defaultAssets.reduce((max, asset) => {
      const currentId = asset.defaultJobAssetID || asset.id;
      return currentId > max ? currentId : max;
    }, 0);

    const newId = highestId + 1;

    setDefaultAssets([
      ...defaultAssets,
      {
        id: newId,
        serviceOfferingID: null,
        assetTypeID: null,
        assetID: null,
        quantity: 0,
        usageDuration: 0,
        costPerUnit: 0,
        pricePerUnit: 0,
      },
    ]);
  };

  const removeAsset = (id) => {
    if (defaultAssets.length > 1) {
      const assetToDelete = defaultAssets.find((dm) => dm.id === id);

      if(assetToDelete.defaultJobAssetID && assetToDelete.defaultJobAssetID > 0){
        const response = handleDeleteDefaultAsset(id);

        if(response){
          fetchDefaultResources(service.serviceOfferingID)
        }
      }else{
        setDefaultAssets(defaultAssets.filter((asset) => asset.id !== id));
      }
    }
  };

  const updateAsset = (id, field, value) => {
    if(field === 'assetID'){
      const selectedAsset = allAssets.find((a) => a.assetID === value);

      setDefaultAssets(
        defaultAssets?.map((asset) =>
          asset.id === id ? { 
            ...asset,
            [field]: value,
            costPerUnit: selectedAsset?.operationalCostsAmount || 0
          } : asset
        )
      );
    }else{
      setDefaultAssets(
        defaultAssets?.map((asset) =>
          asset.id === id ? { ...asset, [field]: value } : asset
        )
      );
    }
  };

  // Other Costs functions
  const addOtherCost = () => {
    const highestId = defaultMiscCosts.reduce((max, miscCost) => {
      const currentId = miscCost.defaultJobMiscCostID || miscCost.id;
      return currentId > max ? currentId : max;
    }, 0);

    const newId = highestId + 1;

    setDefaultMiscCosts([
      ...defaultMiscCosts,
      {
        id: newId,
        serviceOfferingID: null,
        costName: "",
        costAmount: 0,
        customerPrice: 0,
        billToCustomer: false,
      },
    ]);
  };

  const removeOtherCost = (id) => {
    if (defaultMiscCosts.length > 1) {
      const miscCostToDelete = defaultMiscCosts.find((dm) => dm.id === id);

      if(miscCostToDelete.defaultJobMiscCostID && miscCostToDelete.defaultJobMiscCostID > 0){
        const response = handleDeleteDefaultMiscCost(id);

        if(response){
          fetchDefaultResources(service.serviceOfferingID)
        }
      }else{
        setDefaultMiscCosts(defaultMiscCosts.filter((cost) => cost.id !== id));
      }

    }
  };

  const updateOtherCost = (id, field, value) => {
    setDefaultMiscCosts(
      defaultMiscCosts?.map((cost) =>
        cost.id === id ? { ...cost, [field]: value } : cost
      )
    );
  };

  const augmentedIndustryOptions = [
    { industryID: -1, industryName: "+ Add Industry" },
    ...rows?.map((row) => ({
      industryID: row.industryID,
      industryName: row.industryName,
    })),
  ];

  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined) return "";

    const str = value.toString();

    // Allow partial numbers while typing (e.g. ".", "0.", ".5")
    if (str === "" || str === "." || str === "0." || /^\.\d*$/.test(str)) {
      return str;
    }

    // Remove commas and format integer part
    const parts = str.replace(/,/g, "").split(".");
    if (parts[0] !== "") {
      const num = Number(parts[0]);
      // Check if it's actually a valid number before formatting
      if (!isNaN(num)) {
        parts[0] = num.toLocaleString("en-US");
      }
    }

    return parts.join(".");
  };

  const removeCommas = (value) => value.replace(/,/g, "");

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        open={open}
        onClose={handleCloseControl}
        aria-labelledby="form-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            width: "90%",
            maxWidth: "1200px",
            height: "90vh",
            maxHeight: "730px",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Service Configuration
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{ height: "calc(100% - 180px)", overflow: "auto" }}
        >
          {/* Service Details Section */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
              Service Details
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <CustomTextField
                  fullWidth
                  required
                  label="Service Name"
                  placeholder="Enter service name"
                  value={service?.serviceOfferingName || ""}
                  onChange={(e) => {
                    handleServiceChange(
                      "serviceOfferingName",
                      e.target.value
                    );
                  }}
                  error={!!serviceErrors.serviceOfferingName}
                  helperText={serviceErrors.serviceOfferingName}
                  sx={{ maxWidth: "1000px" }}
                />
              </Grid>
            <Grid item xs={12} md={8}>
              <CustomAutocomplete
                fullWidth
                required
                value={
                  augmentedIndustryOptions.find(
                    (industry) =>
                      industry.industryID === service?.industryID
                  ) || null
                }
                onChange={(e, newValue) => {
                  if (newValue && newValue.industryID === -1) {
                    handleOpenIndustryDlg();
                    return;
                  }
                  handleServiceChange(
                    "industryID",
                    newValue ? newValue.industryID : null
                  );
                }}
                options={augmentedIndustryOptions}
                getOptionLabel={(option) => option?.industryName || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.industryID === value?.industryID
                }
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    placeholder="Select Industry"
                    label="Industry"
                    required
                    error={!!serviceErrors.industryID}
                    helperText={serviceErrors.industryID}
                    sx={{ maxWidth: "1000px" }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.industryID}>
                    <span style={{
                      fontWeight: option.industryID === -1 ? 'bold' : 'normal',
                      display: 'block',
                    }}>
                      {option.industryName}
                    </span>
                  </li>
                )}
                renderTags={(value, getTagProps) => {
                  if (!value) return null;
                  return (
                    <Chip
                      label={
                        <Typography variant="body2">
                          {value.industryName}
                        </Typography>
                      }
                      {...getTagProps({ index: 0 })}
                      key={value.industryID}
                    />
                  );
                }}
                sx={{ maxWidth: "1000px" }}
              />
            </Grid>
              <Grid item xs={12} md={8}>
                <CustomTextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  placeholder="Add a description..."
                  value={service?.description || ""}
                  onChange={(e) => {
                    handleServiceChange("description", e.target.value);
                  }}
                  sx={{ maxWidth: "1000px" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  fullWidth
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    Flat Rate
                  <Tooltip 
                  title={
                  <Box>
                   <Typography variant="body2" sx={{ color: "white" }}>
                    This amount will only be used by default if you charge a Flat Rate for this Service. It can always be changed at the time of Estimate or Work Order. If you charge for Time and Materials, this Flat Rate will not be used.
                    </Typography>
                    </Box>
                  }
                    arrow
                    placement="right"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          transform: 'translateX(10px) !important', 
                        }
                      }
                    }}
                  >
                      <IconButton size="small" sx={{ p: 0, color: "text.secondary" }}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  }
                  placeholder="Enter service's flat rate"
                  value={formatNumberWithCommas(service?.flatRate || "")}
                  onChange={(e) => {
                    const rawValue = removeCommas(e.target.value);
                    const cleanedValue = removeLeadingZeros(rawValue);
                    handleServiceChange("flatRate", cleanedValue);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <span style={{ fontSize: "1.3rem" }}>$</span>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: "600px" }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={service.isActive ?? false}
                        onChange={(e) => {
                          handleServiceChange("isActive", e.target.checked);
                        }}
                      />
                      <span className="slider round"></span>
                    </label>
                    <FormLabel sx={{ ml: 2 }}>
                      {service.isActive ? "Active" : "Inactive"}
                    </FormLabel>
                  </Box>
                  <Tooltip
                    arrow
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ color: "white" }}>
                          Toggle between Active and Inactive states for this
                          service. Active means it's currently available for
                          use, while Inactive disables it until reactivated.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" color="info">
                      <Icon
                        icon="tabler:info-circle"
                        style={{ fontSize: 22 }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Resource Details Section */}
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
              Resource Details
            </Typography>

            {/* Materials Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => toggleSection('materials')}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Materials
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expandedSections.materials ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                
                <Collapse in={expandedSections.materials}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "18%" ,textTransform: 'none'  }}>
                              Material Type <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "15%" , textTransform: 'none'  }}>
                              Material Name<span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              UoM <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Per Unit Cost <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "13%", textTransform: 'none' }}>
                              Per Unit Price <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "10%", textTransform: 'none' }}>
                              Quantity <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "8%", textTransform: 'none' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultMaterials?.map((material) => (
                            <TableRow key={material.id}>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  select
                                  placeholder="Select type"
                                  value={material.materialTypeID}
                                  onChange={(e) =>
                                    updateMaterial(
                                      material.id,
                                      "materialTypeID",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  {materialTypes?.map((materialType) => (
                                    <MenuItem
                                      key={materialType.typeCatalogID}
                                      value={materialType.typeCatalogID}
                                    >
                                      {materialType.typeName}
                                    </MenuItem>
                                  ))}
                                </CustomTextField>
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  select
                                  disabled={!material.materialTypeID}
                                  placeholder="Select material"
                                  value={material.materialID}
                                  onChange={(e) =>
                                    updateMaterial(
                                      material.id,
                                      "materialID",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  {allMaterials
                                    ?.filter(
                                      (m) =>
                                        m.materialTypeID ===
                                        material.materialTypeID
                                    )
                                    ?.map((material) => (
                                      <MenuItem
                                        key={material.materialID}
                                        value={material.materialID}
                                      >
                                        {material.itemName}
                                      </MenuItem>
                                    ))}
                                </CustomTextField>
                              </TableCell>
                              <TableCell>
                                <CustomAutocomplete
                                  fullWidth
                                  required
                                  options={unitOptions.sort((a, b) => a.label.localeCompare(b.label))}
                                  value={
                                    unitOptions.find(option => option.value === material?.unitID) || null
                                  }
                                  onChange={(event, newValue) => handleMaterialUnitChange(material.id, newValue)}
                                  getOptionLabel={(option) => option.label}
                                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                                  filterOptions={(options, { inputValue }) => {
                                    const searchTerm = inputValue.toLowerCase();
                                    return options.filter(option => 
                                      option.label.toLowerCase().includes(searchTerm)
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <CustomTextField
                                      {...params}
                                      size="small"
                                      placeholder="Select Unit"
                                      error={!material.unitID}
                                      helperText={!material.unitID ? "Unit is required" : ""}
                                      InputLabelProps={{
                                        sx: {
                                          mb: 2, 
                                        }
                                      }}
                                    />
                                  )}
                                  renderOption={(props, option) => (
                                    <li {...props}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: option.isAddNew ? 'bold' : 'normal',
                                        }}
                                      >
                                        {option.label}
                                      </Typography>
                                    </li>
                                  )}
                                  PaperProps={{
                                    sx: {
                                      maxHeight: 200
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={material.costPerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateMaterial(
                                      material.id,
                                      "costPerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={material.pricePerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateMaterial(
                                      material.id,
                                      "pricePerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0"
                                  value={material.quantity}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateMaterial(
                                      material.id,
                                      "quantity",
                                      rawValue
                                    );
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    removeMaterial(material.id)
                                  }
                                  size="small"
                                >
                                  <Icon icon="tabler:trash" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {defaultMaterials?.length === 0 && (
                        <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            No materials added!
                          </Typography>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={addMaterial}
                          >
                            Add Material
                          </Button>
                        </Box>
                      )}
                    </TableContainer>
                    {defaultMaterials?.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          onClick={addMaterial}
                        >
                          Add Material
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Labor Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => toggleSection('labor')}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Labor
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expandedSections.labor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                
                <Collapse in={expandedSections.labor}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "18%", textTransform: 'none' }}>
                              Skill Type <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "18%", textTransform: 'none' }}>
                              Employee Name <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Man Hours <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "14%", textTransform: 'none' }}>
                              Cost per Hour <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Markup % <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "8%", textTransform: 'none'  }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultLabors?.map((labor) => (
                            <TableRow key={labor.id}>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  select
                                  placeholder="Select skill"
                                  value={labor.skillID}
                                  onChange={(e) =>
                                    updateLabor(
                                      labor.id,
                                      "skillID",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  {skillTypes?.map((skillType) => (
                                    <MenuItem
                                      key={skillType.skillID}
                                      value={skillType.skillID}
                                    >
                                      {skillType.skillName}
                                    </MenuItem>
                                  ))}
                                </CustomTextField>
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  select
                                  placeholder="Select employee"
                                  disabled={!labor.skillID}
                                  value={labor.employeeID}
                                  onChange={(e) =>
                                    updateLabor(
                                      labor.id,
                                      "employeeID",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  {allEmployees
                                    ?.filter((e) => e.skillID === labor.skillID && e.empType !== 'Sub Contractors')
                                    ?.map((emp) => (
                                      <MenuItem
                                        key={emp.employeeID}
                                        value={emp.employeeID}
                                      >
                                        {emp.firstName + " " + emp.lastName}
                                      </MenuItem>
                                    ))}
                                </CustomTextField>
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0"
                                  value={labor.manHours}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateLabor(
                                      labor.id,
                                      "manHours",
                                      rawValue
                                    );
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={labor.costPerHour}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateLabor(
                                      labor.id,
                                      "costPerHour",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                           <TableCell>
                              <CustomTextField
                                fullWidth
                                type="number"
                                placeholder="0"
                                value={labor.markup}
                                onChange={(e) => {
                                  const rawValue = removeLeadingZeros(e.target.value);
                                  updateLabor(labor.id, "markup", rawValue);
                                }}
                                onBlur={(e) => {
                                  const value = e.target.value;
                                  if (value && value.trim() !== '') {
                                    const numericValue = parseFloat(value);
                                    if (!isNaN(numericValue)) {
                                      const roundedValue = Math.round(numericValue);
                                      updateLabor(labor.id, "markup", roundedValue.toString());
                                    }
                                  }
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <span>%</span>
                                    </InputAdornment>
                                  ),
                                }}
                                size="small"
                              />
                            </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() => removeLabor(labor.id)}
                                  size="small"
                                >
                                  <Icon icon="tabler:trash" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {defaultLabors?.length === 0 && (
                        <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            No labor added!
                          </Typography>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={addLabor}
                          >
                            Add Labor
                          </Button>
                        </Box>
                      )}
                    </TableContainer>
                    {defaultLabors?.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          onClick={addLabor}
                        >
                          Add Labor
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Assets Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => toggleSection('assets')}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Assets
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expandedSections.assets ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                
                <Collapse in={expandedSections.assets}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "15%", textTransform: 'none'  }}>
                              Asset Type<span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "15%" , textTransform: 'none' }}>
                              Asset Name <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "10%" , textTransform: 'none' }}>
                              Quantity <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%" , textTransform: 'none' }}>
                              Usage Duration <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none'  }}>
                              Cost per Asset <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "14%" , textTransform: 'none' }}>
                              Price per Asset <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "8%", textTransform: 'none' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultAssets?.map((asset) => (
                            <TableRow key={asset.id}>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  select
                                  placeholder="Select type"
                                  value={asset.assetTypeID}
                                  onChange={(e) =>
                                    updateAsset(
                                      asset.id,
                                      "assetTypeID",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  {assetTypes?.map((assetType) => (
                                    <MenuItem
                                      key={assetType.typeCatalogID}
                                      value={assetType.typeCatalogID}
                                    >
                                      {assetType.typeName}
                                    </MenuItem>
                                  ))}
                                </CustomTextField>
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  select
                                  placeholder="Select asset"
                                  disabled={!asset.assetTypeID}
                                  value={asset.assetID}
                                  onChange={(e) =>
                                    updateAsset(
                                      asset.id,
                                      "assetID",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  {allAssets
                                    ?.filter(
                                      (a) => a.assetTypeID === asset.assetTypeID
                                    )
                                    ?.map((ast) => (
                                      <MenuItem
                                        key={ast.assetID}
                                        value={ast.assetID}
                                      >
                                        {ast.assetName}
                                      </MenuItem>
                                    ))}
                                </CustomTextField>
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0"
                                  value={asset.quantity}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateAsset(
                                      asset.id,
                                      "quantity",
                                      rawValue
                                    );
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  placeholder="e.g., 8 hours"
                                  value={asset.usageDuration}
                                  onChange={(e) =>
                                    updateAsset(
                                      asset.id,
                                      "usageDuration",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={asset.costPerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateAsset(
                                      asset.id,
                                      "costPerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={asset.pricePerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateAsset(
                                      asset.id,
                                      "pricePerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() => removeAsset(asset.id)}
                                  size="small"
                                >
                                  <Icon icon="tabler:trash" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {defaultAssets?.length === 0 && (
                        <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            No assets added!
                          </Typography>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={addAsset}
                          >
                            Add Asset
                          </Button>
                        </Box>
                      )}
                    </TableContainer>
                    {defaultAssets?.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          onClick={addAsset}
                        >
                          Add Asset
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Other Costs Section */}
            <Card>
              <CardContent sx={{ p: '0 !important' }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => toggleSection('otherCosts')}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Other Costs
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expandedSections.otherCosts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                
                <Collapse in={expandedSections.otherCosts}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "25%" , textTransform: 'none' }}>
                              Cost Name<span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "18%" , textTransform: 'none' }}>
                              Cost Amount <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "18%", textTransform: 'none'  }}>
                              Customer Price <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "20%" , textTransform: 'none' }}>
                              Bill to Customer? <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "8%" , textTransform: 'none' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultMiscCosts?.map((cost) => (
                            <TableRow key={cost.id}>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  placeholder="Cost name"
                                  value={cost.costName}
                                  onChange={(e) =>
                                    updateOtherCost(
                                      cost.id,
                                      "costName",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={cost.costAmount}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateOtherCost(
                                      cost.id,
                                      "costAmount",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={cost.customerPrice}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateOtherCost(
                                      cost.id,
                                      "customerPrice",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={cost.billToCustomer}
                                      onChange={(e) =>
                                        updateOtherCost(
                                          cost.id,
                                          "billToCustomer",
                                          e.target.checked
                                        )
                                      }
                                      color="success"
                                    />
                                  }
                                  label="Bill to Customer"
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() => removeOtherCost(cost.id)}
                                  size="small"
                                >
                                  <Icon icon="tabler:trash" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {defaultMiscCosts?.length === 0 && (
                        <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            No other costs added!
                          </Typography>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={addOtherCost}
                          >
                            Add Other Cost
                          </Button>
                        </Box>
                      )}
                    </TableContainer>
                    {defaultMiscCosts?.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          onClick={addOtherCost}
                        >
                          Add Other Cost
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>

            {/* Subcontractor Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    pb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  onClick={() => toggleSection('subcontractor')}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Subcontractor
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {expandedSections.subcontractor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>
                
                <Collapse in={expandedSections.subcontractor}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "18%", textTransform: 'none' }}>
                              Skill Type <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "18%", textTransform: 'none' }}>
                              Subcontractor Name <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "10%", textTransform: 'none' }}>
                              Unit of Measure <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "10%", textTransform: 'none' }}>
                              Unit Quantity <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Cost Per Unit <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Price Per Unit <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "12%", textTransform: 'none' }}>
                              Markup % <span style={{ color: 'red' }}>*</span>
                            </TableCell>
                            <TableCell sx={{ width: "8%", textTransform: 'none'  }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {defaultSubcontractors?.map((subcontractor) => (
                            <TableRow key={subcontractor.id}>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  select
                                  placeholder="Select skill"
                                  value={subcontractor.skillID}
                                  onChange={(e) =>
                                    updateSubcontractor(
                                      subcontractor.id,
                                      "skillID",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  {skillTypes?.map((skillType) => (
                                    <MenuItem
                                      key={skillType.skillID}
                                      value={skillType.skillID}
                                    >
                                      {skillType.skillName}
                                    </MenuItem>
                                  ))}
                                </CustomTextField>
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  select
                                  placeholder="Select subcontractor"
                                  disabled={!subcontractor.skillID}
                                  value={subcontractor.employeeID}
                                  onChange={(e) =>
                                    updateSubcontractor(
                                      subcontractor.id,
                                      "employeeID",
                                      e.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  {subcontractors
                                    ?.filter((e) => e.skillID === subcontractor.skillID)
                                    ?.map((sub) => (
                                      <MenuItem
                                        key={sub.employeeID}
                                        value={sub.employeeID}
                                      >
                                        {sub.firstName + " " + sub.lastName}
                                      </MenuItem>
                                    ))}
                                </CustomTextField>
                              </TableCell>
                              <TableCell>
                                <CustomAutocomplete
                                  fullWidth
                                  required
                                  options={unitOptions.sort((a, b) => a.label.localeCompare(b.label))}
                                  value={
                                    unitOptions.find(option => option.value === subcontractor?.unitID) || null
                                  }
                                  onChange={(event, newValue) => handleSubcontractorUnitChange(subcontractor.id, newValue)}
                                  getOptionLabel={(option) => option.label}
                                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                                  filterOptions={(options, { inputValue }) => {
                                    const searchTerm = inputValue.toLowerCase();
                                    return options.filter(option => 
                                      option.label.toLowerCase().includes(searchTerm)
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <CustomTextField
                                      {...params}
                                      size="small"
                                      placeholder="Select Unit"
                                      error={!subcontractor.unitID}
                                      helperText={!subcontractor.unitID ? "Unit is required" : ""}
                                      InputLabelProps={{
                                        sx: {
                                          mb: 2, 
                                        }
                                      }}
                                    />
                                  )}
                                  renderOption={(props, option) => (
                                    <li {...props}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: option.isAddNew ? 'bold' : 'normal',
                                        }}
                                      >
                                        {option.label}
                                      </Typography>
                                    </li>
                                  )}
                                  PaperProps={{
                                    sx: {
                                      maxHeight: 200
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0"
                                  value={subcontractor.unitQuantity}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateSubcontractor(
                                      subcontractor.id,
                                      "unitQuantity",
                                      rawValue
                                    );
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={subcontractor.costPerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateSubcontractor(
                                      subcontractor.id,
                                      "costPerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <CustomTextField
                                  fullWidth
                                  type="number"
                                  placeholder="0.00"
                                  value={subcontractor.pricePerUnit}
                                  onChange={(e) => {
                                    const rawValue = removeLeadingZeros(e.target.value);
                                    updateSubcontractor(
                                      subcontractor.id,
                                      "pricePerUnit",
                                      rawValue
                                    );
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <span>$</span>
                                      </InputAdornment>
                                    ),
                                  }}
                                  size="small"
                                />
                              </TableCell>
                             <TableCell>
                              <CustomTextField
                                fullWidth
                                type="number"
                                placeholder="0"
                                value={subcontractor.markup}
                                onChange={(e) => {
                                  const rawValue = removeLeadingZeros(e.target.value);
                                  updateSubcontractor(
                                    subcontractor.id,
                                    "markup",
                                    rawValue
                                  );
                                }}
                                onBlur={(e) => {
                                  const value = e.target.value;
                                  if (value && value.trim() !== '') {
                                    const numericValue = parseFloat(value);
                                    if (!isNaN(numericValue)) {
                                      const roundedValue = Math.round(numericValue);
                                      updateSubcontractor(
                                        subcontractor.id,
                                        "markup",
                                        roundedValue.toString()
                                      );
                                    }
                                  }
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <span>%</span>
                                    </InputAdornment>
                                  ),
                                }}
                                size="small"
                              />
                            </TableCell>
                              <TableCell>
                                <IconButton
                                  color="error"
                                  onClick={() => removeSubcontractor(subcontractor.id)}
                                  size="small"
                                >
                                  <Icon icon="tabler:trash" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {defaultSubcontractors?.length === 0 && (
                        <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            No Subcontractor added!
                          </Typography>
                          <Button
                            variant="contained"
                            color="main"
                            startIcon={<AddIcon />}
                            onClick={addSubcontractor}
                          >
                            Add Subcontractor
                          </Button>
                        </Box>
                      )}
                    </TableContainer>
                    {defaultSubcontractors?.length > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="contained"
                          color="main"
                          startIcon={<AddIcon />}
                          onClick={addSubcontractor}
                        >
                          Add Subcontractor
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
            gap: 2,
          }}
          className="dialog-actions-dense"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              width: { xs: "100%", sm: "auto" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <Button
              variant="contained"
              color="info"
              onClick={handleCloseControl}
            >
              Cancel
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              width: { xs: "100%", sm: "auto" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <Button
              variant="tonal"
              color="info"
              disabled={!hasServiceSavePermission}
              {...(hasServiceSavePermission && {
                onClick: handleResetAll,
              })}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="main"
              disabled={!hasServiceSavePermission}
              {...(hasServiceSavePermission && {
               onClick: () => {
                handleSaveService(
                  defaultAssets,
                  defaultMaterials,
                  defaultLabors,
                  defaultSubcontractors,
                  defaultMiscCosts
                );
                resetDefaultResources(); 
              }
              })}
            >
              {serviceContent.btnText}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <AddIndustryDialog
        open={openIndustryDlg}
        handleClose={handleCloseIndustryDlg}
        IndustryProps={IndustryProps}
      />

      {/* Unit Dialog */}
      <UnitDialog
        open={unitDialogOpen}
        handleClose={() => setUnitDialogOpen(false)}
        UnitProps={{
          existingUnit: null,
          fetchUnits: fetchUnitsData,
          unitContent: {
            title: "Add Unit of Measurement",
            entity: 'create'
          },
          onUnitCreated: handleUnitCreatedCallback
        }}
      />

      {/*<ToastContainer />*/}
    </>
  );
};

export default ServiceConfiguration;